import SystemDashboardWidget from '@/models/SystemDashboardWidget'
import moment from 'moment'
import numeral from 'numeral'
import { DeliveryOverviewResponse, fetchDeliveryOverview } from './deliveryContract'

/**
 * KAN-195 — Top headline: month-to-date Disney delivered impressions against the
 * monthly goal, as a labeled progress bar.
 *
 * Counts Disney only (Disney MLB + Disney Non-MLB); Non-Disney is excluded.
 * `goal` falls back to 150M but comes from the backend (config-driven) response.
 */
export default class DeliveryGoalCard extends SystemDashboardWidget {
  public title: string = 'Month Delivery vs Goal (Disney Only)'

  public key: string = 'delivery-goal'

  public view: string = 'card'

  public size = {
    lg: 12,
    md: 12,
  }

  public goal: number = 150_000_000

  constructor(args: any = null) {
    super()

    if (args && args.title) this.title = args.title
    if (args && args.key) this.key = args.key
    if (args && typeof args.goal === 'number') this.goal = args.goal
    if (args && args.size) this.size = args.size
  }

  public async dataSource(): Promise<DeliveryOverviewResponse> {
    return fetchDeliveryOverview(false, this.filters.month)
  }

  protected dataMapper(res: DeliveryOverviewResponse): Promise<any> {
    // Disney only — Disney MLB + Disney Non-MLB, excluding Non-Disney.
    const delivered = res.daily_current.reduce(
      (acc, point) => acc + point.disneymlb + point.disneynonmlb,
      0,
    )

    // Goal comes from the backend (config-driven); fall back to the local default.
    const goal = res.goal || this.goal
    const pct = goal ? delivered / goal : 0
    const width = Math.min(Math.max(pct, 0), 1) * 100
    const hit = pct >= 1
    const barClass = hit ? 'bg-success' : 'bg-primary'

    const html = `
      <div class="d-flex justify-content-between align-items-end mb-2">
        <div>
          <span class="h3 mb-0 font-weight-bold">${numeral(delivered).format('0,0')}</span>
          <span class="text-muted"> delivered of ${numeral(goal).format('0,0')} goal</span>
        </div>
        <div class="h4 mb-0 ${hit ? 'text-success' : 'text-primary'}">${numeral(pct).format('0.0%')}</div>
      </div>
      <div class="progress" style="height: 1.25rem;">
        <div
          class="progress-bar ${barClass}"
          role="progressbar"
          style="width: ${width.toFixed(1)}%;"
          aria-valuenow="${width.toFixed(0)}"
          aria-valuemin="0"
          aria-valuemax="100"
        >${numeral(pct).format('0.0%')}</div>
      </div>
      <div class="text-muted small mt-1">
        Disney delivery vs ${numeral(goal).format('0,0a')} goal · ${moment(res.month || moment().format('YYYY-MM'), 'YYYY-MM').format('MMMM YYYY')}
      </div>
    `

    return Promise.resolve(html)
  }
}
